import React, { useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { Link, useHistory } from "react-router-dom";

import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Button, Form, List, message, Row, Space, Switch } from "antd";

import { UserManagementRoles } from "misc/roles";
import { LoggedInUserContext } from "context/LoggedInUserContext";
import { useUsers, usePermissions, useRoles } from "misc/api/keycloakApi";
import { getQueryParameter } from "misc/helpfunctions";
import { clientUrls } from "misc/urls";
import { fonts } from "../../../styles";
import SideHeader from "navigation/SideHeader";
import { Card } from "misc/card";
import { TextField } from "misc/fields";
import UserEmail from "admin/components/steps/UserEmail";
import UserLocale from "admin/components/steps/UserLocale";
import UserCustomer from "admin/components/steps/UserCustomer";
import UserFirstName from "admin/components/steps/UserFirstName";
import UserLastName from "admin/components/steps/UserLastName";

const Title = styled(TextField)({
    fontSize: fonts.size16
});

const UserComponent = () => {
    const { hasAuthorityFull, hasAuthorityLocalAdmin, activeOrganization } = useContext(LoggedInUserContext);
    const [t] = useTranslation();
    const location = useLocation();
    const history = useHistory();
    const [form] = Form.useForm();
    const organizationId = Form.useWatch("organizationId", form);

    const { users, usersQueryState, createUser, createUserMutation, updateUser, updateUserMutation } = useUsers({ suspense: true });
    const { permissions } = usePermissions({ suspense: true });

    const user = useMemo(() => users.find(user => user.id === getQueryParameter(location, "userId")), [users, location]);

    const steps = useMemo(() => {
        return [
            {
                key: "user-email",
                title: "t_user_email",
                icon: <FontAwesomeIcon icon={["fas", "envelope"]} />,
                content: <UserEmail form={form} name="email" value={user?.email} disabled={user?.email} users={users} />
            },
            {
                key: "user-first-name",
                title: "t_user_first_name",
                icon: "Aa",
                content: <UserFirstName form={form} name="firstName" value={user?.firstName} />
            },
            {
                key: "user-last-name",
                title: "t_user_last_name",
                icon: "Aa",
                content: <UserLastName form={form} name="lastName" value={user?.lastName} />
            },
            {
                key: "user-locale",
                title: "t_user_locale",
                icon: <FontAwesomeIcon icon={["fas", "language"]} />,
                content: <UserLocale form={form} name="locale" value={user?.locale} />
            },
            {
                key: "user-customer",
                title: "t_customer",
                icon: <FontAwesomeIcon icon={["fas", "buildings"]} />,
                content: <UserCustomer form={form} name="organizationId" value={user?.organizations ? user.organizations[0].id : null} />,
                visible: hasAuthorityFull()
            },
            {
                key: "user-management",
                title: "t_user_permissions_extra",
                icon: <FontAwesomeIcon icon={["fas", "lock"]} />,
                content: (
                    <Form form={form} disabled={user?.permissions.some(permission => permission.name === "permission_authority_full")}>
                        <Form.Item form={form} name={"isAdmin"} valuePropName="checked">
                            <Space size="middle" style={{ width: "100%" }}>
                                <Switch
                                    defaultChecked={user?.permissions.some(
                                        permission =>
                                            permission.name === "permission_authority_local-admin" || permission.name === "permission_authority_full"
                                    )}
                                    onChange={checked => {
                                        form.setFieldsValue({ isAdmin: checked });
                                    }}
                                />
                                <TextField value="t_user_management" />
                            </Space>
                        </Form.Item>
                    </Form>
                )
            }
        ];
    }, [user, organizationId]);

    const onClickSave = values => {
        const getLocalAdminPermissionIds = () => {
            if (values.isAdmin)
                return permissions
                    .filter(
                        permission => permission.name === UserManagementRoles.PermissionAuthorityLocalAdmin && permission.client === "usermanagement"
                    )
                    .map(permission => permission.id);
            return [];
        };

        let organizationId;
        if (hasAuthorityFull()) {
            organizationId = values.organizationId || null;
        } else if (hasAuthorityLocalAdmin()) {
            organizationId = activeOrganization?.id || null;
        }

        const payload = {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            locale: values.locale,
            permissions: [...getLocalAdminPermissionIds()],
            organizationId
        };
        user ? onEditUser({ id: user.id, ...payload }) : onCreateUser(payload);
    };
    const onCreateUser = user => {
        createUser(user, {
            onSuccess: () => message.success(t("t_user_create_success")) && history.push(clientUrls.users.baseUrl()),
            onError: error => {
                return message.error(error.error ? error.error : t(`t_http_error_${error.status}`));
            }
        });
    };

    const onEditUser = user => {
        updateUser(user, {
            onSuccess: () => message.success(t("t_user_edit_success")) && history.push(clientUrls.users.baseUrl()),
            onError: error => {
                return message.error(error.error ? error.error : t(`t_http_error_${error.status}`));
            }
        });
    };

    return (
        <SideHeader>
            <Card height={800} stretchHeight title={user ? "t_user_edit_title" : "t_user_create_title"} isLoading={usersQueryState.isLoading}>
                <List
                    split={false}
                    dataSource={steps.filter(step => step.visible !== false)}
                    renderItem={item => (
                        <List.Item key={item.key}>
                            <Space direction="vertical" size={20} style={{ width: "100%" }}>
                                <Space size={12}>
                                    <Avatar size={40}>{item.icon}</Avatar>
                                    <Title value={item.title} />
                                </Space>
                                {item.content}
                            </Space>
                        </List.Item>
                    )}
                />
                <Row type="flex" justify="end">
                    <Form form={form} onFinish={onClickSave}>
                        <Form.Item>
                            <Space direction="horizontal" size={3}>
                                <Link to={clientUrls.users.baseUrl()}>
                                    <Button>{t("t_cancel")}</Button>
                                </Link>
                                <Button type="primary" htmlType="submit" loading={createUserMutation.isLoading || updateUserMutation.isLoading}>
                                    {t("t_save")}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Row>
            </Card>
        </SideHeader>
    );
};

export default UserComponent;
